import { useCallback, useEffect, useState } from 'react'
import { MailCheck, RefreshCw } from 'lucide-react'
import { adminMutate, usePermissions } from '../admin/primitives.js'
import { estEtatSmtp, fmtDateHeure, lireSupervision, messageEchecMutation, type EtatSmtpSonde, type Lecture } from './api.js'

/**
 * COPIE DU SMTP DANS SONDE — le secours e-mail de Sonde envoie avec le même
 * compte que Gestion. Gestion la recopie à chaque enregistrement du profil
 * actif ; si Sonde était injoignable, AUCUN nouvel essai automatique (il
 * faut une confirmation d'identité humaine) : ce bandeau le dit, et propose
 * « Recopier dans Sonde ».
 *
 * Deux habits : `bandeau` (discret, écran Communications) et `panneau`
 * (Intégrations › Sonde, section « Secours e-mail »). Même lecture, même geste.
 *
 * `relire` : la page l'incrémente après un enregistrement de profil. La
 * recopie part juste après la réponse et Sonde teste le SMTP (jusqu'à ~45 s) :
 * on relit tout de suite, puis deux fois plus tard.
 */
export function SondeSmtpCopie({ variante, relire = 0 }: { variante: 'bandeau' | 'panneau'; relire?: number }) {
  const { permissions } = usePermissions()
  const peutRecopier = permissions.includes('sonde.credentials.manage')
  const [l, setL] = useState<Lecture<EtatSmtpSonde> | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const [retour, setRetour] = useState<{ ok: boolean; texte: string } | null>(null)

  const charger = useCallback(() => {
    void lireSupervision('/smtp', estEtatSmtp).then(setL)
  }, [])
  useEffect(() => {
    charger()
    if (relire === 0) return
    const t1 = window.setTimeout(charger, 15_000)
    const t2 = window.setTimeout(charger, 50_000)
    return () => { window.clearTimeout(t1); window.clearTimeout(t2) }
  }, [charger, relire])

  const recopier = async () => {
    setEnvoi(true); setRetour(null)
    try {
      const r = await adminMutate<{ ok?: boolean; message?: string; error?: string }>('POST', '/api/v1/supervision/smtp/push', {})
      if (r.status >= 200 && r.status < 300 && typeof r.data.ok === 'boolean') {
        setRetour({ ok: r.data.ok, texte: r.data.message ?? (r.data.ok ? 'SMTP recopié dans Sonde.' : 'La recopie n’a pas abouti.') })
      } else {
        setRetour({ ok: false, texte: messageEchecMutation(r.status, r.data, 'rien n’a été recopié') })
      }
    } catch {
      setRetour({ ok: false, texte: 'Gestion n’a pas répondu : la recopie n’a peut-être pas eu lieu.' })
    } finally { setEnvoi(false); charger() }
  }

  // Sans le droit de lire Sonde, l'écran Communications reste ce qu'il était.
  if (!l || (variante === 'bandeau' && l.etat === 'refus')) return null

  const texte = l.etat !== 'ok' ? l.message
    : l.data.last_attempt_at === null ? 'jamais faite'
      : l.data.ok === true ? `à jour (recopiée le ${fmtDateHeure(l.data.last_attempt_at)})`
        : `en attente — dernier essai le ${fmtDateHeure(l.data.last_attempt_at)}${l.data.message ? ` : ${l.data.message}` : ''}`
  const aJour = l.etat === 'ok' && l.data.ok === true
  const bouton = peutRecopier && l.etat !== 'refus' ? (
    <button type="button" className="lc-btn lc-btn--ghost" disabled={envoi} onClick={() => { void recopier() }}>
      <RefreshCw size={14} aria-hidden="true" className={envoi ? 'cm-spin' : ''} /> {envoi ? 'Recopie… (jusqu’à une minute)' : 'Recopier dans Sonde'}
    </button>
  ) : null

  if (variante === 'bandeau') {
    return (
      <div className={`sv-bandeau${aJour ? ' sv-bandeau--ok' : ''}`} role="status">
        <MailCheck size={16} aria-hidden="true" />
        <span className="sv-bandeau__texte">
          <strong>Copie du SMTP dans Sonde :</strong> {texte}
          {retour ? <span className="sv-bandeau__retour"> — {retour.texte}</span> : null}
        </span>
        {bouton}
      </div>
    )
  }
  return (
    <div className="sv-pile">
      <p className={`cle-verdict${aJour ? ' cle-verdict--ok' : l.etat === 'ok' && l.data.ok === false ? ' cle-verdict--doute' : ''}`} role="status">
        Copie du SMTP dans Sonde : {texte}
      </p>
      <p className="lc-hint">
        Le secours e-mail de Sonde (quand Gestion ne peut plus prévenir) envoie avec le compte SMTP actif de Gestion.
        Gestion le recopie à chaque enregistrement d’un profil ; le mot de passe est lu au coffre au moment de l’envoi ; il n’est gardé que dans le coffre de Sonde. Sonde garde le dernier SMTP recopié tant qu’un autre ne le remplace pas.
      </p>
      {bouton ? <div className="cle-actions">{bouton}</div> : null}
      {retour ? <p role="status" className={`cle-verdict ${retour.ok ? 'cle-verdict--ok' : 'cle-verdict--doute'}`}>{retour.texte}</p> : null}
    </div>
  )
}
